import { useEffect, useId, useRef, useState } from 'react';

import { Button } from './Button';
import { Input } from './Input';
import { Modal } from './Modal';

export interface ConfirmDialogProps {
    open: boolean;
    onClose: () => void;
    onConfirm: () => void;
    title: string;
    description?: string;
    confirmLabel?: string;
    cancelLabel?: string;
    // `danger` paints the confirm button red. `default` is blue.
    variant?: 'default' | 'danger';
    /**
     * Opt-in friction gate for destructive actions: when true the operator must
     * retype a freshly-shown 3-digit code before the confirm button enables. A
     * new code is generated each time the dialog opens.
     */
    requireCodeConfirmation?: boolean;
}

function randomCode(): string {
    // 100–999 — always three visible digits, no leading-zero typing ambiguity.
    return String(Math.floor(100 + Math.random() * 900));
}

// Composes `Modal` with a labelled card body and two buttons. The confirm
// button is the initial focus target so keyboard users can hit Enter; Tab
// cycles between the two buttons. Backdrop click / Escape / Cancel all close
// without firing onConfirm. With `requireCodeConfirmation`, a code input gates
// the confirm and takes initial focus instead.
export function ConfirmDialog({
    open,
    onClose,
    onConfirm,
    title,
    description,
    confirmLabel = 'Confirma',
    cancelLabel = 'Anuleaza',
    variant = 'default',
    requireCodeConfirmation = false,
}: ConfirmDialogProps) {
    const confirmRef = useRef<HTMLButtonElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const titleId = useId();
    const codeId = useId();

    const [code, setCode] = useState('');
    const [entered, setEntered] = useState('');

    // Fresh code (and a cleared field) each time the dialog opens.
    useEffect(() => {
        if (open && requireCodeConfirmation) {
            setCode(randomCode());
            setEntered('');
        }
    }, [open, requireCodeConfirmation]);

    const codeOk = !requireCodeConfirmation || entered.trim() === code;

    const confirm = () => {
        if (!codeOk) return;
        onConfirm();
        onClose();
    };

    return (
        <Modal
            open={open}
            onClose={onClose}
            initialFocusRef={requireCodeConfirmation ? inputRef : confirmRef}
            ariaLabelledBy={titleId}
            // Confirms are commonly opened from inside another `Dialog`
            // (which portals to body, sibling-after `.v2-root` where Modal
            // portals into). Bump above Dialog's z-50 so the confirm
            // surface paints on top regardless of portal-target ordering.
            className="z-[60]"
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className="w-full max-w-sm rounded-lg border border-solid border-gray-200 bg-white p-5 shadow-xl dark:border-gray-700 dark:bg-gray-800"
            >
                <h2 id={titleId} className="text-base font-semibold text-gray-900 dark:text-gray-100">
                    {title}
                </h2>
                {description && (
                    <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">{description}</p>
                )}
                {requireCodeConfirmation && (
                    <div className="mt-4">
                        <label
                            htmlFor={codeId}
                            className="block text-sm text-gray-600 dark:text-gray-300"
                        >
                            Tasteaza codul{' '}
                            <span className="select-none font-mono text-base font-semibold tracking-widest text-gray-900 dark:text-gray-100">
                                {code}
                            </span>{' '}
                            pentru a confirma
                        </label>
                        <Input
                            ref={inputRef}
                            id={codeId}
                            inputMode="numeric"
                            autoComplete="off"
                            value={entered}
                            onChange={(e) => setEntered(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') confirm();
                            }}
                            placeholder="Cod de confirmare"
                            className="mt-2"
                        />
                    </div>
                )}
                <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                    <Button variant="secondary" onClick={onClose} className="sm:flex-none">
                        {cancelLabel}
                    </Button>
                    <Button
                        ref={confirmRef}
                        variant={variant === 'danger' ? 'danger' : 'primary'}
                        disabled={!codeOk}
                        onClick={confirm}
                        className="sm:flex-none"
                    >
                        {confirmLabel}
                    </Button>
                </div>
            </div>
        </Modal>
    );
}
